import { ModelInputs, textKeys, numberKeys, dateKeys, boolKeys } from "../domain/types";
import { Field } from "./Field";
import { useAuth } from "../../../auth/AuthProvider";

// Keys that get special Field rendering instead of plain text input
const SPECIAL_FIELD_KEYS = new Set<keyof ModelInputs>(["eia_ba_code"]);

interface ProjectDetailFormProps {
    details: ModelInputs;
    onChange: (name: keyof ModelInputs, value: any) => void;
}

// Boolean keys hidden for partner accounts (summary access)
const PARTNER_HIDDEN_BOOL_KEYS = new Set(["existing_project_flag"]);

export function ProjectDetailForm({ details, onChange }: ProjectDetailFormProps) {
    const { isPartner } = useAuth();

    const visibleBoolKeys = (boolKeys as unknown as (keyof ModelInputs)[]).filter(
        k => !(isPartner && PARTNER_HIDDEN_BOOL_KEYS.has(k))
    );

    return (
        <div className="flex-1 overflow-y-auto pr-2 space-y-4">
            {/* Partner notice */}
            {isPartner && (
                <div className="rounded-lg bg-quantum-500/10 border border-quantum-500/20 p-3">
                    <p className="text-caption text-quantum-400">
                        Partner accounts are limited to future (projected) projects.
                    </p>
                </div>
            )}

            {/* text inputs */}
            {(textKeys as unknown as (keyof ModelInputs)[]).map(k =>
                SPECIAL_FIELD_KEYS.has(k) ? (
                    <Field key={k} name={k} value={details[k]} onChange={onChange} />
                ) : (
                    <div key={k} className="flex flex-col">
                        <label htmlFor={k} className="input-label capitalize">
                            {k.replace(/_/g, " ")}
                        </label>
                        <input
                            id={k}
                            name={k}
                            value={String(details[k] || '')}
                            onChange={(e) => onChange(k, e.target.value)}
                            className="input"
                        />
                    </div>
                )
            )}

            {/* numeric inputs */}
            {(numberKeys as unknown as (keyof ModelInputs)[]).map(k => (
                <div key={k} className="flex flex-col">
                    <label htmlFor={k} className="input-label capitalize">
                        {k.replace(/_/g, " ")}
                    </label>
                    <input
                        id={k}
                        name={k}
                        type="number"
                        step="any"
                        value={String(details[k] || '')}
                        onChange={(e) => onChange(k, e.target.value)}
                        className="input"
                    />
                </div>
            ))}

            {/* date inputs */}
            {(dateKeys as unknown as (keyof ModelInputs)[]).map(k => (
                <div key={k} className="flex flex-col">
                    <label htmlFor={k} className="input-label capitalize">
                        {k.replace(/_/g, " ")}
                    </label>
                    <input
                        id={k}
                        name={k}
                        type="date"
                        value={String(details[k] || '')}
                        onChange={(e) => onChange(k, e.target.value)}
                        className="input"
                    />
                </div>
            ))}

            {/* booleans (filtered for partners) */}
            {visibleBoolKeys.map(k => (
                <div key={k} className="flex items-center gap-3">
                    <input
                        id={k}
                        name={k}
                        type="checkbox"
                        checked={Boolean(details[k])}
                        onChange={(e) => onChange(k, e.target.checked)}
                        className="h-4 w-4 accent-quantum-500"
                    />
                    <label htmlFor={k} className="text-sm font-medium text-text-secondary capitalize">
                        {k.replace(/_/g, " ")}
                    </label>
                </div>
            ))}
        </div>
    );
}